/**
 * DeliveryModal.jsx — Deliverable-based delivery management
 *
 * Shows checklist of deliverable lines from calcSnapshot.
 * Each row has a checkbox + link input. One link can cover multiple deliverables.
 * Tracks per-deliverable sent status in job.deliveryStatus JSONB.
 *
 * On save:
 *   - All checked → status = 'delivered'
 *   - Some checked → status = 'partial_delivery'
 *   - Sends delivery email with links to client
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, TextField, IconButton, Tooltip, Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { isValidTransition } from '../../lib/statusTransitions';
import { normalizeSnapshot } from '../../lib/calcEngine';
import { splitDeliverables } from '../../lib/jobDeliverables';
import { openEmail } from '../../lib/emailTemplates';
import { useAuth } from '../../hooks/useAuth';
import { useMobile } from '../../hooks/useMobile';

export default function DeliveryModal({ open, onClose, job, settings, onUpdateJob, onStatusChange }) {
  const { isMobile } = useMobile();
  const { profile } = useAuth();
  const senderName = profile?.full_name || '';
  // Build deliverable lines from calcSnapshot
  const deliverables = useMemo(() => {
    if (!job) return [];
    const ns = normalizeSnapshot(job.calcSnapshot);
    const dlLines = ns.deliverableLines || [];
    if (dlLines.length > 0) {
      return dlLines.map((dl, i) => ({
        id: i,
        desc: dl.desc || `Deliverable ${i + 1}`,
      }));
    }
    if (job.deliverables) {
      return splitDeliverables(job.deliverables).map((desc, i) => ({
        id: i,
        desc,
      }));
    }
    return [];
  }, [job?.calcSnapshot, job?.deliverables]);

  const [items, setItems] = useState([]);
  const [sharedLink, setSharedLink] = useState('');
  const [overrides, setOverrides] = useState(new Set()); // indices with per-item link overrides

  useEffect(() => {
    if (!open) return;
    const existing = Array.isArray(job?.deliveryStatus) ? job.deliveryStatus : [];
    // Detect if there's a common link across sent items
    const links = existing.filter((e) => e.sent && e.link).map((e) => e.link);
    const uniqueLinks = [...new Set(links)];
    const commonLink = uniqueLinks.length === 1 ? uniqueLinks[0] : '';

    const newItems = deliverables.map((del) => {
      const saved = existing.find((e) => e.desc === del.desc);
      return {
        desc: del.desc,
        sent: saved?.sent || false,
        link: saved?.link || '',
        sentAt: saved?.sentAt || null,
      };
    });

    setItems(newItems);
    setSharedLink(commonLink);
    // Items whose link differs from the common link get an override
    const ov = new Set();
    newItems.forEach((item, i) => {
      if (item.link && item.link !== commonLink) ov.add(i);
    });
    setOverrides(ov);
  }, [open, deliverables, job?.deliveryStatus]);

  const sentCount = items.filter((i) => i.sent).length;
  const allSent = items.length > 0 && sentCount === items.length;

  const handleToggle = (idx) => {
    setItems((prev) => prev.map((item, i) =>
      i === idx ? { ...item, sent: !item.sent, sentAt: !item.sent ? new Date().toISOString() : null } : item
    ));
  };

  const handleToggleAll = () => {
    const target = !allSent;
    setItems((prev) => prev.map((item) => ({
      ...item,
      sent: target,
      sentAt: target ? (item.sentAt || new Date().toISOString()) : null,
    })));
  };

  const toggleOverride = (idx) => {
    setOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
        // Clear per-item link when removing override (inherits shared)
        setItems((p) => p.map((item, i) => i === idx ? { ...item, link: '' } : item));
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleOverrideLinkChange = (idx, value) => {
    setItems((prev) => prev.map((item, i) =>
      i === idx ? { ...item, link: value } : item
    ));
  };

  // Resolve final link for each item: override link or shared link
  const resolveLink = (item, idx) => {
    if (overrides.has(idx) && item.link) return item.link;
    return sharedLink;
  };

  const handleSave = (sendEmail = false) => {
    const now = new Date().toISOString();
    const statusData = items.map((item, idx) => ({
      desc: item.desc,
      sent: item.sent,
      link: item.sent ? resolveLink(item, idx) : '',
      sentAt: item.sent ? (item.sentAt || now) : null,
    }));

    const links = [...new Set(statusData.filter((d) => d.sent && d.link).map((d) => d.link))].join('\n');

    const updates = {
      id: job.id,
      deliveryStatus: statusData,
      deliveryLinks: links,
    };

    const currentSentCount = statusData.filter((d) => d.sent).length;
    const currentAllSent = statusData.length > 0 && currentSentCount === statusData.length;
    const currentSomeSent = currentSentCount > 0 && !currentAllSent;

    let newStatus = null;
    if (currentAllSent && isValidTransition(job.status, 'delivered')) {
      newStatus = 'delivered';
    } else if (currentSomeSent && isValidTransition(job.status, 'partial_delivery')) {
      newStatus = 'partial_delivery';
    }

    onUpdateJob?.(updates);
    if (newStatus) onStatusChange?.(newStatus);

    if (sendEmail && job.contactEmail && links) {
      const sentItems = statusData.filter((d) => d.sent);
      const templateId = currentAllSent ? 'delivery_complete' : 'delivery_partial';
      openEmail(templateId, {
        toEmail: job.contactEmail,
        job: { ...job, deliveryLinks: links },
        settings,
        senderName,
        delivery: {
          deliveredCount: String(currentSentCount),
          totalCount: String(statusData.length),
          deliveredItems: sentItems.map((d) => `• ${d.desc}`).join('\n'),
        },
      });
    }

    onClose();
  };

  if (!job) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, pb: 0 }}>
        Manage Deliverables
        {items.length > 0 && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {sentCount}/{items.length} sent
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No deliverable lines found. Add deliverables via the Quote Builder or edit the job.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* ── Delivery link ─────────────────────────────── */}
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.75, display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                Delivery Link
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="https://drive.google.com/... or Dropbox, WeTransfer, etc."
                value={sharedLink}
                onChange={(e) => setSharedLink(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem' }}>
                Applies to all checked items{overrides.size > 0 ? ` (${overrides.size} with custom link)` : ''}
              </Typography>
            </Box>

            {/* ── Checklist ────────────────────────────────── */}
            <Box>
              {/* Select all header */}
              <Box
                onClick={handleToggleAll}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5,
                  cursor: 'pointer', borderRadius: '4px 4px 0 0',
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {allSent ? (
                  <CheckBoxIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                )}
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
                  {allSent ? 'Deselect all' : 'Select all'}
                </Typography>
              </Box>

              {/* Items */}
              {items.map((item, idx) => {
                const hasOverride = overrides.has(idx);
                const effectiveLink = resolveLink(item, idx);
                return (
                  <Box key={idx}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1, py: 0.75,
                        borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: item.sent ? 'rgba(46,139,87,0.04)' : 'transparent',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <Checkbox
                        checked={item.sent}
                        onChange={() => handleToggle(idx)}
                        size="small"
                        sx={{ p: { xs: 1, md: 0.25 } }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1, minWidth: 0, fontSize: '0.8rem', fontWeight: 500,
                          textDecoration: item.sent ? 'line-through' : 'none',
                          color: item.sent ? 'text.disabled' : 'text.primary',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {item.desc}
                      </Typography>
                      {/* Sent date chip */}
                      {item.sentAt && (
                        <Chip
                          label={new Date(item.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          size="small"
                          sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(46,139,87,0.08)', color: 'success.dark' }}
                        />
                      )}
                      {/* Link status indicator + override toggle */}
                      <Tooltip title={hasOverride ? 'Remove custom link (use shared)' : 'Set custom link for this item'} arrow>
                        <IconButton
                          size="small"
                          onClick={() => toggleOverride(idx)}
                          sx={{
                            p: { xs: 1, md: 0.25 },
                            color: hasOverride ? 'primary.main' : (effectiveLink ? 'success.main' : 'text.disabled'),
                          }}
                        >
                          {hasOverride ? <LinkOffIcon sx={{ fontSize: 16 }} /> : <LinkIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* Per-item link override (expandable) */}
                    {hasOverride && (
                      <Box sx={{ pl: 4.5, pr: 1, py: 0.75, bgcolor: 'rgba(25,118,210,0.03)', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Custom link for this item..."
                          value={item.link}
                          onChange={(e) => handleOverrideLinkChange(idx, e.target.value)}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleSave(false)}
          disabled={items.length === 0}
        >
          Save
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => handleSave(true)}
          disabled={items.length === 0 || sentCount === 0 || !job.contactEmail}
        >
          Save & Send Email
        </Button>
      </DialogActions>
    </Dialog>
  );
}
